import type { PlaybackQualityName, PlaybackSampleIntervalMs } from "@situate/contracts/runtime";
import { PLAYBACK_SAMPLE_INTERVAL_MS } from "@situate/contracts/runtime";

export type PlaybackQuality = PlaybackQualityName;

export interface PlaybackQualityOption {
  readonly value: PlaybackQuality;
  readonly label: string;
  readonly sampleIntervalMs: PlaybackSampleIntervalMs;
}

interface PlaybackQualityPreset {
  readonly maximumDurationMs: number;
  readonly quality: PlaybackQuality;
}

interface PlaybackDateRangeConfig {
  readonly minimumDurationMs: number;
  readonly maximumDurationMs: number;
  readonly qualityPresets: readonly PlaybackQualityPreset[];
  readonly qualityOptions: readonly PlaybackQualityOption[];
}

const MINUTE_MS = 60 * 1_000;
const TEN_MINUTES_MS = 10 * MINUTE_MS;
const THIRTY_MINUTES_MS = 30 * MINUTE_MS;
const NINETY_MINUTES_MS = 90 * MINUTE_MS;
const FIVE_HOURS_MS = 5 * 60 * MINUTE_MS;
const TEN_HOURS_MS = 10 * 60 * MINUTE_MS;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * MINUTE_MS;

export const PLAYBACK_DATE_RANGE_CONFIG = {
  minimumDurationMs: TEN_MINUTES_MS,
  maximumDurationMs: TWENTY_FOUR_HOURS_MS,
  qualityPresets: [
    { maximumDurationMs: THIRTY_MINUTES_MS, quality: "best" },
    { maximumDurationMs: NINETY_MINUTES_MS, quality: "high" },
    { maximumDurationMs: FIVE_HOURS_MS, quality: "balanced" },
    { maximumDurationMs: TEN_HOURS_MS, quality: "low" },
    { maximumDurationMs: TWENTY_FOUR_HOURS_MS, quality: "very_low" },
  ],
  qualityOptions: [
    { value: "best", label: "Best (1s)", sampleIntervalMs: PLAYBACK_SAMPLE_INTERVAL_MS.best },
    { value: "high", label: "High (2s)", sampleIntervalMs: PLAYBACK_SAMPLE_INTERVAL_MS.high },
    {
      value: "balanced",
      label: "Balanced (5s)",
      sampleIntervalMs: PLAYBACK_SAMPLE_INTERVAL_MS.balanced,
    },
    { value: "low", label: "Low (10s)", sampleIntervalMs: PLAYBACK_SAMPLE_INTERVAL_MS.low },
    {
      value: "very_low",
      label: "Very low (15s)",
      sampleIntervalMs: PLAYBACK_SAMPLE_INTERVAL_MS.very_low,
    },
  ],
} as const satisfies PlaybackDateRangeConfig;

export function getPlaybackQualityForDuration(durationMs: number): PlaybackQuality {
  const preset = PLAYBACK_DATE_RANGE_CONFIG.qualityPresets.find(
    (candidate) => durationMs <= candidate.maximumDurationMs
  );
  if (preset !== undefined) return preset.quality;

  const fallbackPreset = PLAYBACK_DATE_RANGE_CONFIG.qualityPresets.at(-1);
  if (fallbackPreset === undefined) throw new Error("Playback quality presets are required");
  return fallbackPreset.quality;
}

export function isPlaybackQuality(value: string): value is PlaybackQuality {
  return PLAYBACK_DATE_RANGE_CONFIG.qualityOptions.some((option) => option.value === value);
}

export function getPlaybackQualityOption(quality: PlaybackQuality): PlaybackQualityOption {
  const option = PLAYBACK_DATE_RANGE_CONFIG.qualityOptions.find(
    (candidate) => candidate.value === quality
  );
  if (!option) throw new Error(`Unsupported playback quality: ${quality}`);
  return option;
}
